import { ReportSnapshot, ReportRow } from '../services/reportsService';

/**
 * Export snapshot data as formatted CSV file
 * Includes summary metrics section followed by detail rows
 * When `detailRows` is provided, only those rows are written to the detailed section
 */
export const exportSnapshotToCsv = (snapshot: ReportSnapshot, detailRows?: ReportRow[]): void => {
  const rows: string[] = [];
  const reportRows = detailRows ?? snapshot.reportData;
  const isFiltered = reportRows.length !== snapshot.reportData.length;

  // Title and metadata
  rows.push(`COMPLIANCE SNAPSHOT REPORT - ${snapshot.reportDate}`);
  rows.push(`Generated: ${new Date(snapshot.generatedAt).toLocaleString()}`);
  rows.push('');

  // Summary metrics section
  rows.push('SUMMARY METRICS');
  rows.push(`Total APIs Monitored,"${snapshot.totalApisMonitored}"`);
  rows.push(`Total APIs Analyzed,"${snapshot.totalApisAnalyzed}"`);
  rows.push(`Not Analyzed,"${snapshot.notAnalyzedCount}"`);
  rows.push(`Overall Compliance Rate,"${snapshot.overallComplianceRate.toFixed(1)}%"`);
  rows.push('');

  // Compliance breakdown
  rows.push('COMPLIANCE BREAKDOWN');
  rows.push(`Compliant,"${snapshot.complianceStats.compliant}"`);
  rows.push(`Partially Compliant,"${snapshot.complianceStats.partiallyCompliant}"`);
  rows.push(`Non-Compliant,"${snapshot.complianceStats.nonCompliant}"`);
  rows.push(`Not Analyzed,"${snapshot.complianceStats.notAnalyzed}"`);
  rows.push('');

  // Findings summary
  rows.push('FINDINGS SUMMARY');
  rows.push(`Total Findings,"${snapshot.totalFindingsCount}"`);
  rows.push(`Average Findings per API,"${snapshot.averageFindingsPerApi.toFixed(2)}"`);
  rows.push(`Errors,"${snapshot.findingsBySeverity.errors}"`);
  rows.push(`Warnings,"${snapshot.findingsBySeverity.warnings}"`);
  rows.push(`Infos,"${snapshot.findingsBySeverity.info}"`);
  rows.push('');

  // Detailed API report
  rows.push(isFiltered
    ? `DETAILED API REPORT (filtered: ${reportRows.length} of ${snapshot.reportData.length} APIs)`
    : 'DETAILED API REPORT');
  
  const headers = [
    'API ID',
    'API Name',
    'Line of Business',
    'Product',
    'Area',
    'Version',
    'Point of Contact',
    'Pipeline Enrolled',
    'Lifecycle Stage',
    'Status',
    'Quality Score (%)',
    'Compliance Score (%)',
    'Findings Count',
    'Last Analyzed',
  ];

  rows.push(headers.map(h => escapeCSVValue(h)).join(','));

  // Add detail rows — flatten child versions so each version is its own row
  if (reportRows.length > 0) {
    const flattenedRows: ReportRow[] = [];
    reportRows.forEach((row) => {
      if (row.childVersions && row.childVersions.length > 0) {
        row.childVersions.forEach((version) => {
          flattenedRows.push({
            ...row,
            apiId: version.versionId,
            apiVersion: version.versionNumber,
            status: version.status || row.status,
            qualityScore: version.qualityScore !== undefined ? version.qualityScore : row.qualityScore,
            complianceScore: version.complianceScore !== undefined ? version.complianceScore : row.complianceScore,
            findings: version.findings || row.findings,
            lastAnalyzedAt: version.lastAnalyzedAt || row.lastAnalyzedAt,
            isEnrolled: version.isEnrolled !== undefined ? version.isEnrolled : row.isEnrolled,
            lifecycleStage: (version as any).lifecycleStage || row.lifecycleStage,
          });
        });
      } else {
        flattenedRows.push(row);
      }
    });

    flattenedRows.forEach((row) => {
      const csvRow = [
        row.apiId,
        row.fullApiName,
        row.lineOfBusiness || '',
        row.product || '',
        row.area || '',
        row.apiVersion || '',
        row.pointOfContact || '',
        row.isEnrolled ? 'Yes' : 'No',
        row.lifecycleStage || '',
        row.status || '',
        row.qualityScore?.toFixed(1) || '',
        row.complianceScore?.toFixed(1) || '',
        (row.findings?.length || 0).toString(),
        row.lastAnalyzedAt || '',
      ];

      rows.push(csvRow.map(cell => escapeCSVValue(cell)).join(','));
    });
  }

  // Generate and download file
  const csv = rows.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `api-compliance-snapshot-${snapshot.reportDate}${isFiltered ? '-filtered' : ''}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Escape CSV values - handle quotes and commas properly
 */
function escapeCSVValue(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined) {
    return '';
  }

  const stringValue = value.toString();
  // Neutralize spreadsheet formula injection by prefixing with single quote
  const safeValue = /^[=+\-@\t\r]/.test(stringValue) ? `'${stringValue}` : stringValue;

  // Quote values containing CSV delimiters after neutralizing spreadsheet formulas
  if (safeValue.includes(',') || safeValue.includes('\n') || safeValue.includes('\r') || safeValue.includes('"')) {
    return `"${safeValue.replace(/"/g, '""')}"`;
  }

  return safeValue;
}
